import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  Injectable,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';

@Injectable()
class AppErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    // ngx-bootstrap v21 bug: animationState changes from 'void' after position
    // calculation, which Angular dev-mode catches. Safe to suppress — never
    // occurs in production builds.
    if (error instanceof Error && error.message.includes('@datepickerAnimation')) {
      return;
    }
    super.handleError(error);
  }
}

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations'; // NOSONAR
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { CartSaveStatusInterceptor } from './core/interceptors/cart-save-status.interceptor';
import { HttpLoadingInterceptor } from './core/interceptors/http-loading.interceptor';
import { RateLimitRetryInterceptor } from './core/interceptors/rate-limit-retry.interceptor';
import { AppPageTitleStrategy } from './core/services/app-page-title.strategy';
import { ParamRouteReuseStrategy } from './core/services/param-route-reuse.strategy';
import { AuthInterceptor } from './core/services/auth.interceptor';
import { AuthService } from '@core/services/auth.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: HttpLoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CartSaveStatusInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // Last, so it sits closest to the network: a 429 it rides out never reaches the three above
    // as a failure, leaving the loader up and the cart's save status honest while it waits.
    { provide: HTTP_INTERCEPTORS, useClass: RateLimitRetryInterceptor, multi: true },
    provideAppInitializer(() => inject(AuthService).initSession()),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: AppPageTitleStrategy },
    { provide: RouteReuseStrategy, useClass: ParamRouteReuseStrategy },
    provideAnimations(), // NOSONAR
    // Empty root store: feature state is registered per lazy chunk via `provideState`, so a
    // reducer only exists once the feature that owns it has been loaded.
    provideStore(),
    provideEffects(),
    // Time-travel debugging. `logOnly` in production leaves the timeline readable without letting
    // anyone dispatch or rewind against a live session.
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideTranslateService(),
  ],
};
